import React, { useState } from 'react';
import styled from 'styled-components/macro';
import { Button, DatePicker, Form, Input, message, Modal } from 'antd';
import { useBatchUpdateDeprecationMutation } from '../../../../graphql/mutations.generated';
import { handleBatchError } from '../utils';
import { useTranslation } from '../../../../i18n/useTranslation';

const StyledDatePicker = styled(DatePicker)`
    .ant-picker-input input {
        font-size: 14px !important;
    }
`;

type Props = {
    urns: string[];
    onClose: () => void;
    refetch?: () => void;
};

export const UpdateDeprecationModal = ({ urns, onClose, refetch }: Props) => {
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(false);
    const [batchUpdateDeprecation] = useBatchUpdateDeprecationMutation();
    const [form] = Form.useForm();

    const handleClose = () => {
        form.resetFields();
        onClose();
    };

    const handleOk = async (formData: any) => {
        setIsLoading(true);
        try {
            await batchUpdateDeprecation({
                variables: {
                    input: {
                        resources: [...urns.map((urn) => ({ resourceUrn: urn }))],
                        deprecated: true,
                        note: formData.note,
                        decommissionTime: formData.decommissionTime && formData.decommissionTime.unix(),
                    },
                },
            });
            message.destroy();
            message.success({ content: t('Deprecation updated'), duration: 2 });
        } catch (e: unknown) {
            message.destroy();
            if (e instanceof Error) {
                message.error(
                    handleBatchError(urns, e, {
                        content: `${t('Failed to update deprecation')}: \n ${e.message || ''}`,
                        duration: 2,
                    }),
                );
            }
        }
        refetch?.();
        handleClose();
    };

    return (
        <Modal
            title={t('ADD_DEPRECATION_DETAILS')}
            visible
            onCancel={handleClose}
            keyboard
            centered
            className="f-modal"
            footer={
                <>
                    <Button onClick={handleClose} type="default">
                        {t('Cancel')}
                    </Button>
                    <Button form="addDeprecationForm" key="submit" htmlType="submit" type="primary" loading={isLoading}>
                        {t('Ok')}
                    </Button>
                </>
            }
        >
            <Form form={form} name="addDeprecationForm" onFinish={handleOk} layout="vertical">
                <Form.Item name="note" label={t('Note')} rules={[{ whitespace: true }, { min: 0, max: 100 }]}>
                    <Input placeholder={t('Add Note')} autoFocus />
                </Form.Item>
                <Form.Item name="decommissionTime" label={t('Decommission Date')}>
                    <StyledDatePicker className="ant-input" placeholder={t('Select Date')} />
                </Form.Item>
            </Form>
        </Modal>
    );
};
